<template>
  <div
    id="instruction-select"
    class="selection-field uk-flex"
    :class="{ 'discard-active': discardExpand }"
  >
    <span
      uk-icon="icon: close;ratio: 1.5"
      class="uk-padding-small uk-position-absolute uk-position-right"
      @click="handleClose"
      style="height: 50px; cursor: pointer"
    />
    <div
      id="discard-area"
      class="discard-area uk-flex"
      :class="{ 'uk-flex-center': !discardExpand }"
      @dragover.prevent
      @dragenter.prevent
      @drop="drop"
    >
      <div class="uk-flex uk-flex-column uk-flex-between discard-area-left">
        <div class="uk-text-center uk-text-small uk-text-bold" style="cursor: default">Discard</div>
        <button
          uk-icon="icon:trash;ratio:1.5"
          class="discard-area-buttons"
          style="cursor: default"
        />
        <button
          @click="discardExpand = !discardExpand"
          :uk-icon="`icon:${discardExpand ? 'shrink' : 'expand'};ratio:1.5`"
          class="discard-area-buttons"
        />
      </div>
      <div
        v-if="discardExpand"
        class="discard-area-right uk-flex uk-flex-wrap uk-position-relative"
      >
        <template v-for="(reply, index) in discardedReplies" :key="index">
          <TextBubble
            class="text-box uk-position-absolute"
            :style="{
              top: getBoxPosition(index + 1, 2, true),
            }"
            :draggable="true"
            @dragstart="startDrag($event, reply)"
          >
            <div>
              <div class="uk-badge uk-light uk-background-secondary">
                {{ reply.author.name }}
              </div>
              <div class="markup-highlihgt--neutral">
                {{ reply.comment.replace(regex, '') }}
              </div>
            </div>
            <div
              v-for="citation in annotationCitations(reply.author._id)"
              :key="citation._id"
              class="citation uk-flex uk-flex-nowrap uk-flex-between uk-width-1-1 uk-position-relative"
            >
              <span
                class="citation-link uk-flex uk-flex-nowrap uk-flex-row uk-link uk-link-text uk-display-inline-block uk-text-left"
                @click="EventBus.emit('citation.readOnly', citation, annotation, 'annotation')"
              >
                <span v-if="citation.disputed" class="uk-margin-small-right" uk-icon="comment">
                </span>
                <span>{{ citation.title }}</span>
              </span>
            </div>
          </TextBubble>
        </template>
      </div>
    </div>
    <div
      :key="updated"
      id="non-select-area"
      class="non-select-area uk-flex uk-flex-wrap uk-position-relative"
      @dragover.prevent
      @dragenter.prevent
      @drop="drop"
    >
      <template v-for="(reply, index) in unSelectedReplies" :key="index">
        <TextBubble
          class="text-box uk-position-absolute"
          :style="{
            top: getBoxPosition(index + 1, 2),
          }"
          :draggable="true"
          @dragstart="startDrag($event, reply)"
        >
          <div>
            <div class="uk-badge uk-light uk-background-secondary">
              {{ reply.author.name }}
            </div>
            <div class="markup-highlihgt--neutral">
              {{ reply.comment.replace(regex, '') }}
            </div>
          </div>
          <div
            v-for="citation in annotationCitations(reply.author._id)"
            :key="citation._id"
            class="citation uk-flex uk-flex-nowrap uk-flex-between uk-width-1-1 uk-position-relative"
          >
            <span
              class="citation-link uk-flex uk-flex-nowrap uk-flex-row uk-link uk-link-text uk-display-inline-block uk-text-left"
              @click="EventBus.emit('citation.readOnly', citation, annotation, 'annotation')"
            >
              <span v-if="citation.disputed" class="uk-margin-small-right" uk-icon="comment">
              </span>
              <span>{{ citation.title }}</span>
            </span>
          </div>
        </TextBubble>
      </template>
    </div>
    <img
      @click="changeAnnotation('prev')"
      :uk-tooltip="'Click to begin selecting responses for the previous inline instruction.'"
      src="/icons/icon-skip-up.svg"
      class="uk-position-absolute"
      style="left: 50%; top: 3%; z-index: 3; cursor: pointer"
    />
    <img
      @click="changeAnnotation('next')"
      :uk-tooltip="'Click to begin selecting responses for the next inline instruction.'"
      src="/icons/icon-skip-down.svg"
      class="uk-position-absolute"
      style="left: 50%; bottom: 3%; z-index: 3; cursor: pointer"
    />
    <div class="select-container uk-flex uk-flex-column">
      <p class="uk-padding-small uk-text-center uk-text-bold uk-margin-remove">Select</p>
      <div
        :key="updated"
        id="select-area"
        class="select-container-area uk-position-relative"
        @dragover.prevent
        @dragenter.prevent
        @drop="drop"
      >
        <template v-for="(reply, index) in selectedReplies" :key="index">
          <TextBubble
            class="text-box uk-position-absolute"
            :style="{
              top: getBoxPosition(index + 1, 3),
            }"
            :draggable="true"
            @dragstart="startDrag($event, reply)"
          >
            <div>
              <div class="uk-badge uk-light uk-background-secondary">
                {{ reply.author.name }}
              </div>
              <div class="markup-highlihgt--neutral">
                {{ reply.comment.replace(regex, '') }}
              </div>
            </div>
            <div
              v-for="citation in annotationCitations(reply.author._id)"
              :key="citation._id"
              class="citation uk-flex uk-flex-nowrap uk-flex-between uk-width-1-1 uk-position-relative"
            >
              <span
                class="citation-link uk-flex uk-flex-nowrap uk-flex-row uk-link uk-link-text uk-display-inline-block uk-text-left"
                @click="EventBus.emit('citation.readOnly', citation, annotation, 'annotation')"
              >
                <span v-if="citation.disputed" class="uk-margin-small-right" uk-icon="comment">
                </span>
                <span>{{ citation.title }}</span>
              </span>
            </div>
          </TextBubble>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import TextBubble from '@/components/display/general/TextBubble';
import { EventBus } from '@/assets/js/event-bus';
import { mapState, mapMutations, mapActions } from 'vuex';

export default {
  name: 'SelectionField',

  components: {
    TextBubble,
  },

  props: {
    annotation: Object,
    counterpoints: Array,
  },

  data() {
    return {
      replies: [],
      regex: /<[/]?p>/gi,
      counter: 1,
      updated: 0,
      discardExpand: false,
    };
  },

  mounted() {
    this.replies = this.annotation.teamReplies || [];
    this.replies?.map(n => {
      if (n.status === 'accepted as it is' || n.status === 'accepted') {
        EventBus.emit('team.accepted.comment', {
          id: this.annotation._id,
          comment: n.comment.replace(this.regex, '').trim(),
          status: n.status,
        });
      }
    });
    this.updated++;
    EventBus.on('selection.modal.close', this.handleClose);
  },
  beforeUnmount() {
    EventBus.off('selection.modal.close', this.handleClose);
  },
  computed: {
    EventBus() {
      return EventBus;
    },

    ...mapState(['citations', 'selectedInstruction']),

    discardedReplies() {
      return this.replies.filter(reply => reply.status === 'discarded');
    },

    selectedReplies() {
      return this.replies.filter(
        reply => reply.status === 'accepted' || reply.status === 'accepted as it is'
      );
    },

    unSelectedReplies() {
      return this.replies.filter(reply => reply.status === 'unchanged' || reply.status === null);
    },
  },

  methods: {
    ...mapMutations(['hideTeamReplies', 'setSelectedInstruction', 'incrementScrollCounter']),

    ...mapActions(['showToastAlert']),

    annotationCitations(id) {
      if (!this.citations[this.annotation._id]) return [];
      return this.citations[this.annotation._id].filter(ciation => ciation.createdBy._id === id);
    },

    handleClose() {
      this.hideTeamReplies();
      this.$emit('close');
      this.setSelectedInstruction({});
    },

    getBoxPosition(index, offset, isDiscard) {
      let counter = this.counter;
      if (index === 1) {
        counter = index;
      }
      if (this.discardExpand && !isDiscard) {
        return `${index * 10}%`;
      }
      if (index % offset === 0) {
        if (this.counter !== counter) {
          this.counter++;
        }
        return `${this.counter * 10}%`;
      }
      return `${this.counter * 10}%`;
    },

    startDrag(event, reply) {
      let from = null;
      let selectElement = document.getElementById('select-area');
      let discardElement = document.getElementById('discard-area');
      if (selectElement.contains(event.srcElement)) {
        from = 'accepted';
      } else if (discardElement.contains(event.srcElement)) {
        from = 'discarded';
      } else {
        from = 'unchanged';
      }
      event.dataTransfer.setData('data', JSON.stringify({ id: reply._id, from }));
    },

    drop(event) {
      let data = JSON.parse(event.dataTransfer.getData('data'));
      let to = null;
      let selectElement = document.getElementById('select-area');
      let discardElement = document.getElementById('discard-area');
      if (selectElement.contains(event.srcElement)) {
        to = 'accepted';
      } else if (discardElement.contains(event.srcElement)) {
        to = 'discarded';
      } else {
        to = 'unchanged';
      }
      if (data.from === to) return;

      if (to === 'accepted') {
        if (this.selectedReplies.length === 3) {
          this.showToastAlert({
            title: 'You can select at most three suggested annotations.',
          });
          return;
        }
      }
      this.replies = this.replies.map(reply => {
        if (reply._id === data.id) {
          reply.status = to;
          if (data.from === 'accepted') {
            EventBus.emit('team.instruction.comment', {
              id: this.annotation._id,
              comment: reply.comment.replace(this.regex, '').trim(),
              status: 'removed',
            });
          } else if (to === 'accepted') {
            EventBus.emit('team.instruction.comment', {
              id: this.annotation._id,
              comment: reply.comment.replace(this.regex, '').trim(),
              status: 'added',
            });
          }
        }
        return reply;
      });

      this.updated++;
    },

    changeAnnotation(direction) {
      let counterpoint = this.counterpoints.filter(
        data => data._id === this.selectedInstruction.cpId
      )[0];
      let annotations = counterpoint.annotations.filter(ann => ann.type === 'instruction');
      let currAnnIndex = annotations.findIndex(x => x._id === this.selectedInstruction._id);
      let updatedIndex = null;
      if (currAnnIndex === annotations.length - 1 && direction === 'next') {
        updatedIndex = 0;
      } else if (currAnnIndex === 0 && direction === 'prev') {
        updatedIndex = annotations.length - 1;
      } else if (direction === 'next') {
        updatedIndex = currAnnIndex + 1;
      } else {
        updatedIndex = currAnnIndex - 1;
      }
      EventBus.emit('annotation.teamreplies', annotations[updatedIndex]);
      this.setSelectedInstruction({
        _id: annotations[updatedIndex]._id,
        cpId: annotations[updatedIndex].cpId,
      });
      this.incrementScrollCounter();
      setTimeout(() => {
        this.replies = this.annotation.teamReplies || [];
        this.replies?.map(n => {
          if (n.status === 'accepted as it is' || n.status === 'accepted') {
            EventBus.emit('team.accepted.comment', {
              id: this.annotation._id,
              comment: n.comment.replace(this.regex, '').trim(),
              status: n.status,
            });
          }
        });
        this.updated++;
      }, 500);
    },
  },
};
</script>
<style lang="sass">
.discard-active
  .discard-area
    width: 50%
    &-left
      width: 8%
    &-right
      color: black
      .text-box
        width: 47%
        &:nth-child(even)
          right: 1%
          margin-right: 1%
        &:nth-child(odd)
          left: 1%
          margin-left: 1%
  .non-select-area, .select-container
    width: 25%
    .text-box
      width: 95% !important
      margin: 0 !important
      left: 3% !important
  .select-container
    width: 25%

.selection-field
  position: absolute
  background-color: #C2E3F2
  z-index: 999
  border: 1px solid #2C3C49
  border-radius: 33px
  height: 30vh
  width: 96%

.discard-area
  padding: 1% 0
  width: 5%
  border-right: 2px dashed #FF0000
  font-size: $global-small-font-size
  color: #F0506E
  &-buttons
    text-align: center
    cursor:pointer
    margin: 0 33% !important
  &-right
    width: 92%

.non-select-area
  width: 42%
  padding: 1%
  font-size: $global-small-font-size
  .text-box
    width: 47%
    &:nth-child(even)
      right: 1%
      margin-right: 1%
    &:nth-child(odd)
      left: 1%
      margin-left: 1%

.select-container
  border-left: 2px dashed #00BB3A
  width: 53%
  font-size: 13px
  color: #00BB3A
  &-area
    width: 100%
    height: 100%
  .text-box
    color: black
    width: 30% !important
    &:nth-child(3n-2)
      left: 1%
      margin-left: 1%
    &:nth-child(3n-1)
      left: 34%
      margin-left: 1%
    &:nth-child(3n)
      right: 1%
      margin-right: 1%

.text-box
  &:hover
    z-index: 2
</style>

<style lang="sass">
.text-box
  .text-bubble
    padding-top: 0
.select-container
  .text-box
    .text-bubble
      border-color: #00BB3A
.non-select-area
  .text-box
    .text-bubble
      border-color: #2D4756
.discard-area-right
  .text-box
    .text-bubble
      border-color: #F0506E

.text-box
  .markup-highlihgt--neutral
    height: 64px
    min-height: 64px
    overflow: hidden
    -webkit-line-clamp: 3
    display: -webkit-box
    -webkit-box-orient: vertical
  &:hover, &:nth-last-child(-n+2)
    .markup-highlihgt--neutral
      height: unset
      display: block
</style>
